import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  LinearProgress,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  AttachMoney as AttachMoneyIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api, { agentService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Agent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  agentCode: string;
  signups?: number;
  commission?: number;
  status: string;
  createdAt: string;
}

const Agents: React.FC = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [newAgent, setNewAgent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    idNumber: '',
    dateOfBirth: '',
    gender: '',
    region: '',
    commissionRate: 5.0,
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: ''
    },
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    }
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching agents at:', new Date().toISOString());
      console.log('🔗 API endpoint: /temp-agents/list');
      
      // Force cache bypass
      const timestamp = Date.now();
      const data = await agentService.getAll();
      
      console.log('📊 Raw API response:', data);
      console.log('📊 Response type:', typeof data);
      console.log('📊 Is array?', Array.isArray(data));
      console.log('📊 Agent details:', data);
      
      if (Array.isArray(data)) {
        // Force React to re-render by creating new array
        setAgents([...data]);
        console.log(`✅ Agents loaded successfully: ${data.length} agents found at ${new Date().toISOString()}`);
        
        // Log each agent
        data.forEach((agent, index) => {
          console.log(`Agent ${index + 1}:`, `${agent.firstName} ${agent.lastName}`, agent.email);
        });
        
        if (data.length === 0) {
          setError('No agents found. Create your first agent!');
        }
      } else {
        console.error('❌ Invalid response format:', data);
        setError('Invalid response format from server');
        setAgents([]);
      }
    } catch (err: any) {
      console.error('❌ Agents API failed:', err);
      console.error('❌ Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url
      });
      
      setError(`Failed to load agents: ${err.response?.data?.error || err.message}`);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAgent = (agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    if (agent) {
      setEditingAgent(agent);
      // Set form data for editing
      setNewAgent({
        firstName: agent.firstName || '',
        lastName: agent.lastName || '',
        email: agent.email,
        phone: agent.phone,
        idNumber: '',
        dateOfBirth: '',
        gender: '',
        region: '',
        commissionRate: agent.commission || 5.0,
        address: {
          street: '',
          city: '',
          province: '',
          postalCode: ''
        },
        emergencyContact: {
          name: '',
          phone: '',
          relationship: ''
        }
      });
      setEditModalOpen(true);
    }
  };

  const handleDeleteAgent = (agentId: string) => {
    setAgentToDelete(agentId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      setDeleting(true);
      setError(null);
      console.log('Deleting agent:', agentToDelete);

      // Delete agent using admin API
      await api.delete(`/admin/users/${agentToDelete}`);
      console.log('Agent deleted successfully');

      setDeleteConfirmOpen(false);
      setAgentToDelete(null);

      // Refresh the agents list
      setTimeout(() => {
        fetchAgents();
      }, 500);

    } catch (error: any) {
      console.error('Delete agent error:', error);
      setError(`Failed to delete agent: ${error.response?.data?.error || error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateAgent = async () => {
    if (!editingAgent) return;

    try {
      setEditing(true);
      setError(null);

      console.log('Updating agent:', editingAgent.id, newAgent);

      // Update agent using admin API
      await api.put(`/admin/agents/${editingAgent.id}`, newAgent);
      console.log('Agent updated successfully');
      
      // Reset form
      setNewAgent({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        idNumber: '',
        dateOfBirth: '',
        gender: '',
        region: '',
        commissionRate: 5.0,
        address: {
          street: '',
          city: '',
          province: '',
          postalCode: ''
        },
        emergencyContact: {
          name: '',
          phone: '',
          relationship: ''
        }
      });
      
      // Close modal
      setEditModalOpen(false);
      setEditingAgent(null);
      
      // Refresh agents list
      setTimeout(() => {
        console.log('🔄 Refreshing agents list after update...');
        fetchAgents();
      }, 500);
      
    } catch (error: any) {
      console.error('Update agent error:', error);
      setError(`Failed to update agent: ${error.message}`);
    } finally {
      setEditing(false);
    }
  };

  const handleCreateAgent = async () => {
    try {
      setCreating(true);
      setError(null);
      
      // Check authentication first
      const token = localStorage.getItem('adminToken');
      const user = localStorage.getItem('adminUser');
      console.log('Auth check - Token exists:', !!token);
      console.log('Auth check - User exists:', !!user);
      
      if (!token) {
        setError('Not authenticated - please log in again');
        return;
      }
      
      // Validation - all required fields based on database schema
      const requiredFields = [
        { field: 'firstName', label: 'First Name' },
        { field: 'lastName', label: 'Last Name' },
        { field: 'email', label: 'Email' },
        { field: 'phone', label: 'Phone' },
        { field: 'idNumber', label: 'ID Number' },
        { field: 'dateOfBirth', label: 'Date of Birth' },
        { field: 'gender', label: 'Gender' }
      ];
      
      const missingFields = requiredFields
        .filter(({ field }) => !newAgent[field as keyof typeof newAgent])
        .map(({ label }) => label);
        
      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      // Validate date format
      const birthDate = new Date(newAgent.dateOfBirth);
      if (isNaN(birthDate.getTime())) {
        setError('Please enter a valid date of birth');
        return;
      }
      
      // Validate gender
      if (!['male', 'female', 'other'].includes(newAgent.gender)) {
        setError('Please select a valid gender');
        return;
      }

      console.log('Creating agent:', newAgent);
      const response = await agentService.create(newAgent);
      console.log('Agent created successfully:', response);
      
      // Reset form
      setNewAgent({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        idNumber: '',
        dateOfBirth: '',
        gender: '',
        region: '',
        commissionRate: 5.0,
        address: {
          street: '',
          city: '',
          province: '',
          postalCode: ''
        },
        emergencyContact: {
          name: '',
          phone: '',
          relationship: ''
        }
      });
      
      // Close modal
      setCreateModalOpen(false);
      
      // Show success message
      console.log('✅ Agent created with temporary password:', response.temporaryPassword);
      
      // Force refresh agents list after a short delay
      setTimeout(() => {
        console.log('🔄 Refreshing agents list after creation...');
        fetchAgents();
      }, 500);
      
    } catch (err: any) {
      console.error('Create agent error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      
      // More detailed error handling
      let errorMessage = 'Failed to create agent';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required - please log in again';
      } else if (err.response?.status === 403) {
        errorMessage = 'Permission denied - insufficient privileges';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const stats = [
    {
      title: 'Total Agents',
      value: agents.length.toString(),
      icon: <GroupIcon />,
      color: '#1976d2'
    },
    {
      title: 'Active Agents',
      value: agents.filter(agent => agent.status === 'active').length.toString(),
      icon: <PersonAddIcon />,
      color: '#2e7d32'
    },
    {
      title: 'Total Signups',
      value: agents.reduce((total, agent) => total + (agent.signups || 0), 0).toLocaleString(),
      icon: <TrendingUpIcon />,
      color: '#ed6c02'
    },
    {
      title: 'Total Commission',
      value: `R${agents.reduce((total, agent) => total + (agent.commission || 0), 0).toLocaleString()}`,
      icon: <AttachMoneyIcon />,
      color: '#9c27b0'
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Agent Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchAgents}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add New Agent
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        {stats.map((stat, index) => (
          <Box key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: stat.color, mr: 2 }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div">
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      <Paper sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          All Agents
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="center">Signups</TableCell>
                <TableCell align="center">Commission</TableCell>
                <TableCell align="center">Performance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                {user?.role === 'super_admin' && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{`${agent.firstName} ${agent.lastName}`}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{agent.email}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {agent.phone}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{agent.signups}</TableCell>
                  <TableCell align="center">R{agent.commission || 0}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={((agent.signups || 0) / 50) * 100}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {agent.signups || 0}/50 target
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={agent.status}
                      color="success"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(agent.createdAt).toLocaleDateString()}</TableCell>
                  {user?.role === 'super_admin' && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditAgent(agent.id)}
                          sx={{ minWidth: 'auto' }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteAgent(agent.id)}
                          sx={{ minWidth: 'auto' }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent Leaderboard
            </Typography>
            {agents.map((agent, index) => (
              <Box key={agent.id} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ mr: 2, minWidth: 30 }}>
                  #{index + 1}
                </Typography>
                <Avatar sx={{ mr: 2 }}>{agent.firstName?.charAt(0) || 'A'}</Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1">{`${agent.firstName || ''} ${agent.lastName || ''}`}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agent.signups || 0} signups
                  </Typography>
                </Box>
                <Typography variant="h6" color="primary">
                  R{agent.commission || 0}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
        
        <Box>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" paragraph>
                <strong>Sarah Johnson</strong> signed up 3 new members
                <Typography variant="caption" color="text.secondary" display="block">
                  2 hours ago
                </Typography>
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Mike Williams</strong> reached 30 signups milestone
                <Typography variant="caption" color="text.secondary" display="block">
                  5 hours ago
                </Typography>
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Lisa Brown</strong> signed up 2 Gold members
                <Typography variant="caption" color="text.secondary" display="block">
                  1 day ago
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Create Agent Modal */}
      <Dialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create New Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Required Personal Information */}
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Required Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              <TextField
                label="First Name *"
                value={newAgent.firstName}
                onChange={(e) => setNewAgent({ ...newAgent, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name *"
                value={newAgent.lastName}
                onChange={(e) => setNewAgent({ ...newAgent, lastName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Email *"
                type="email"
                value={newAgent.email}
                onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Phone *"
                value={newAgent.phone}
                onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                fullWidth
                required
                placeholder="+27821234567"
              />
              <TextField
                label="ID Number *"
                value={newAgent.idNumber}
                onChange={(e) => setNewAgent({ ...newAgent, idNumber: e.target.value })}
                fullWidth
                required
                placeholder="8001015009087"
              />
              <TextField
                label="Date of Birth *"
                type="date"
                value={newAgent.dateOfBirth}
                onChange={(e) => setNewAgent({ ...newAgent, dateOfBirth: e.target.value })}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <FormControl fullWidth required>
                <InputLabel>Gender *</InputLabel>
                <Select
                  value={newAgent.gender}
                  onChange={(e) => setNewAgent({ ...newAgent, gender: e.target.value })}
                  label="Gender *"
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Commission Rate (%)"
                type="number"
                value={newAgent.commissionRate}
                onChange={(e) => setNewAgent({ ...newAgent, commissionRate: parseFloat(e.target.value) || 5.0 })}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Box>

            {/* Optional Agent Information */}
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Agent Information (Optional)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              <TextField
                label="Region"
                value={newAgent.region}
                onChange={(e) => setNewAgent({ ...newAgent, region: e.target.value })}
                fullWidth
                placeholder="e.g., Cape Town, Johannesburg"
              />
            </Box>

            {/* Optional Address Information */}
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Address (Optional)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              <TextField
                label="Street Address"
                value={newAgent.address.street}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  address: { ...newAgent.address, street: e.target.value }
                })}
                fullWidth
                placeholder="123 Main Street"
              />
              <TextField
                label="City"
                value={newAgent.address.city}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  address: { ...newAgent.address, city: e.target.value }
                })}
                fullWidth
                placeholder="Cape Town"
              />
              <TextField
                label="Province"
                value={newAgent.address.province}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  address: { ...newAgent.address, province: e.target.value }
                })}
                fullWidth
                placeholder="Western Cape"
              />
              <TextField
                label="Postal Code"
                value={newAgent.address.postalCode}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  address: { ...newAgent.address, postalCode: e.target.value }
                })}
                fullWidth
                placeholder="7700"
              />
            </Box>

            {/* Optional Emergency Contact */}
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Emergency Contact (Optional)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <TextField
                label="Emergency Contact Name"
                value={newAgent.emergencyContact.name}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  emergencyContact: { ...newAgent.emergencyContact, name: e.target.value }
                })}
                fullWidth
                placeholder="John Doe"
              />
              <TextField
                label="Emergency Contact Phone"
                value={newAgent.emergencyContact.phone}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  emergencyContact: { ...newAgent.emergencyContact, phone: e.target.value }
                })}
                fullWidth
                placeholder="+27821234567"
              />
              <TextField
                label="Relationship"
                value={newAgent.emergencyContact.relationship}
                onChange={(e) => setNewAgent({ 
                  ...newAgent, 
                  emergencyContact: { ...newAgent.emergencyContact, relationship: e.target.value }
                })}
                fullWidth
                placeholder="Spouse, Parent, etc."
              />
            </Box>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAgent}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <PersonAddIcon />}
          >
            {creating ? 'Creating...' : 'Create Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setAgentToDelete(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this agent? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setAgentToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAgent}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Agent Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingAgent(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Edit Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Required Personal Information */}
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              Agent Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
              <TextField
                label="First Name *"
                value={newAgent.firstName}
                onChange={(e) => setNewAgent({ ...newAgent, firstName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last Name *"
                value={newAgent.lastName}
                onChange={(e) => setNewAgent({ ...newAgent, lastName: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Email *"
                type="email"
                value={newAgent.email}
                onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Phone *"
                value={newAgent.phone}
                onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                fullWidth
                required
                placeholder="+27821234567"
              />
              <TextField
                label="Commission Rate (%)"
                type="number"
                value={newAgent.commissionRate}
                onChange={(e) => setNewAgent({ ...newAgent, commissionRate: parseFloat(e.target.value) || 5.0 })}
                fullWidth
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
              <TextField
                label="Region"
                value={newAgent.region}
                onChange={(e) => setNewAgent({ ...newAgent, region: e.target.value })}
                fullWidth
                placeholder="e.g., Cape Town, Johannesburg"
              />
            </Box>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setEditModalOpen(false);
              setEditingAgent(null);
            }} 
            disabled={editing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateAgent}
            variant="contained"
            disabled={editing}
            startIcon={editing ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {editing ? 'Updating...' : 'Update Agent'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agents;